/**
 * Documents section of the Toolbox tab.
 *
 * Installers see the full list they have access to (filtered by brand +
 * category). Staff see everything AND get an upload button plus per-row
 * delete actions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, FileText, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';

const CATEGORY_TONES = {
  risk_assessment: 'bg-rose-100 text-rose-700 border-rose-200',
  install_guide:   'bg-blue-100 text-blue-700 border-blue-200',
  tech_data:       'bg-amber-100 text-amber-700 border-amber-200',
  certification:   'bg-emerald-100 text-emerald-700 border-emerald-200',
  other:           'bg-slate-100 text-slate-700 border-slate-200',
};

export default function DocumentsSection({ initialBrand = 'all', lockBrand = false }) {
  const { user } = useAuth();
  const isStaff = user && ['admin', 'manager', 'team'].includes(user.role);
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({ categories: {}, brands: [] });
  const [brand, setBrand] = useState(initialBrand);
  const [category, setCategory] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (brand && brand !== 'all') params.set('brand', brand);
    if (category && category !== 'all') params.set('category', category);
    const r = await fetch(`${API_URL}/api/toolbox/docs?${params.toString()}`, { credentials: 'include' });
    if (r.ok) setDocs(await r.json());
  }, [brand, category]);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/toolbox/categories`, { credentials: 'include' });
      if (r.ok) setMeta(await r.json());
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    const r = await fetch(`${API_URL}/api/toolbox/docs/${doc.id}/download`, { credentials: 'include' });
    if (!r.ok) return toast.error('Download failed');
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || doc.title;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    const r = await fetch(`${API_URL}/api/toolbox/docs/${doc.id}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) return toast.error('Delete failed');
    toast.success('Document deleted');
    load();
  };

  const groups = useMemo(() => {
    const g = {};
    for (const d of docs) {
      const k = d.category;
      (g[k] ||= []).push(d);
    }
    return g;
  }, [docs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {!lockBrand && (
            <select value={brand} onChange={(e) => setBrand(e.target.value)}
              className="border-2 rounded-lg px-3 py-1.5 text-sm font-medium bg-white" data-testid="doc-brand-filter">
              <option value="all">All trades</option>
              <option value="probound">ProBound</option>
              <option value="proepoxy">ProEpoxy</option>
              <option value="prodecor">ProDecor</option>
              <option value="protraining">ProTraining</option>
            </select>
          )}
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="border-2 rounded-lg px-3 py-1.5 text-sm font-medium bg-white" data-testid="doc-cat-filter">
            <option value="all">All categories</option>
            {Object.entries(meta.categories).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        {isStaff && (
          <Button onClick={() => setUploadOpen(true)} className="bg-[#27292E] text-white hover:bg-[#3a3d45]" data-testid="doc-upload-open">
            <Upload className="w-4 h-4 mr-1" /> Upload document
          </Button>
        )}
      </div>

      {docs.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">
          No documents match those filters yet.{isStaff && ' Click "Upload document" to add one.'}
        </Card>
      ) : (
        Object.entries(groups).map(([catKey, items]) => (
          <div key={catKey}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              {meta.categories[catKey] || catKey}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((d) => (
                <Card key={d.id} className="p-3 flex items-start gap-3" data-testid={`doc-${d.id}`}>
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" title={d.title}>{d.title}</p>
                    {d.description && <p className="text-xs text-slate-500 line-clamp-2">{d.description}</p>}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${CATEGORY_TONES[d.category] || CATEGORY_TONES.other}`}>
                        {meta.categories[d.category] || d.category}
                      </span>
                      {d.brand && d.brand !== 'all' && (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                          {d.brand}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <Button size="sm" variant="outline" onClick={() => download(d)} className="h-7 px-2 text-xs" data-testid={`doc-dl-${d.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                      {isStaff && (
                        <Button size="sm" variant="outline" onClick={() => remove(d)} className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {uploadOpen && <UploadModal meta={meta} defaultBrand={lockBrand ? brand : 'all'} onClose={() => setUploadOpen(false)} onDone={load} />}
    </div>
  );
}

function UploadModal({ meta, defaultBrand = 'all', onClose, onDone }) {
  const [form, setForm] = useState({ title: '', description: '', category: 'risk_assessment', brand: defaultBrand });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error('Choose a file first');
    if (!form.title.trim()) return toast.error('Give it a title');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('category', form.category);
      fd.append('brand', form.brand);
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/toolbox/docs`, { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) throw new Error(formatApiError(await r.json()) || 'Upload failed');
      toast.success('Document uploaded');
      onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">Upload document</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" /></div>
          <div><Label>Description (optional)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border-2 rounded-lg px-3 py-2 text-sm font-medium bg-white" data-testid="doc-cat">
                {Object.entries(meta.categories).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select>
            </div>
            <div>
              <Label>Trade</Label>
              <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="w-full border-2 rounded-lg px-3 py-2 text-sm font-medium bg-white" data-testid="doc-brand">
                <option value="all">All trades</option>
                <option value="probound">ProBound</option>
                <option value="proepoxy">ProEpoxy</option>
                <option value="prodecor">ProDecor</option>
                <option value="protraining">ProTraining</option>
              </select>
            </div>
          </div>
          <div>
            <Label>File</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
            <p className="text-[11px] text-slate-500 mt-1">Max 25MB. PDF / image / DOC / XLS accepted.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#27292E] text-white" data-testid="doc-submit">
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
